import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator, TextInput, Switch } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, stages:'/api/data/crm_stages', deals:'/api/data/crm_deals', activities:'/api/data/crm_activities' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function StageManagementScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('stages');
  const [showStageModal, setShowStageModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stageAnalytics, setStageAnalytics] = useState({ totalDeals:0, avgConversion:0, avgCycleTime:0, bottleneck:null });
  const [newStage, setNewStage] = useState({ name:'', color:'#3B82F6', probability_weight:50, is_active:true });

  const colorOptions = ['#94a3b8','#3b82f6','#f59e0b','#8b5cf6','#ec4899','#10b981','#ef4444','#06b6d4','#f97316','#6366f1'];

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,stagesRes,dealsRes,activitiesRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.stages),api.get(EP.deals,{params:p}),api.get(EP.activities,{params:p}),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    let stgs=safeData(stagesRes.status==='fulfilled'?stagesRes.value:null);
    if(stgs.length===0)stgs=[
      {id:1,stage_id:'lead',name:'Lead',color:'#94a3b8',icon:'person-add-outline',display_order:1,is_active:true,probability_weight:10},
      {id:2,stage_id:'contacted',name:'Contacted',color:'#3b82f6',icon:'phone',display_order:2,is_active:true,probability_weight:25},
      {id:3,stage_id:'qualified',name:'Qualified',color:'#f59e0b',icon:'check-circle',display_order:3,is_active:true,probability_weight:50},
      {id:4,stage_id:'proposal',name:'Proposal',color:'#8b5cf6',icon:'file-document',display_order:4,is_active:true,probability_weight:65},
      {id:5,stage_id:'negotiation',name:'Negotiation',color:'#ec4899',icon:'handshake',display_order:5,is_active:true,probability_weight:80},
      {id:6,stage_id:'closed_won',name:'Closed Won',color:'#10b981',icon:'trophy',display_order:6,is_active:true,probability_weight:100},
      {id:7,stage_id:'closed_lost',name:'Closed Lost',color:'#ef4444',icon:'close-circle',display_order:7,is_active:true,probability_weight:0},
    ];
    setStages(stgs.sort((a,b)=>(a.display_order||0)-(b.display_order||0)));

    const dls=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const acts=safeData(activitiesRes.status==='fulfilled'?activitiesRes.value:null);
    setDeals(dls);
    setActivities(acts);

    // Analytics
    const stageCount={};dls.forEach(d=>{stageCount[d.stage||'unknown']=(stageCount[d.stage||'unknown']||0)+1;});
    const maxStage=Object.entries(stageCount).sort((a,b)=>b[1]-a[1])[0];
    const openDeals=dls.filter(d=>!['closed_won','closed_lost'].includes(d.stage));
    const avgDays=openDeals.length>0?Math.round(openDeals.reduce((s,d)=>s+Math.max(1,Math.floor((Date.now()-new Date(d.created_at).getTime())/(1000*60*60*24))),0)/openDeals.length):0;
    const wonDeals=dls.filter(d=>d.stage==='closed_won').length;
    setStageAnalytics({
      totalDeals:dls.length,
      avgConversion:dls.length>0?(wonDeals/dls.length*100):0,
      avgCycleTime:avgDays,
      bottleneck:maxStage?{stage:maxStage[0],count:maxStage[1]}:null,
    });
  };

  const createStage=async()=>{
    if(!newStage.name){Alert.alert('Required','Stage name is required');return;}
    try{
      const stageId=String(newStage.name ?? '').toLowerCase().replace(/\s+/g,'_');
      await api.post(EP.stages,{
        stage_id:stageId,name:newStage.name,color:newStage.color,
        display_order:stages.length+1,is_active:newStage.is_active,
        probability_weight:parseInt(newStage.probability_weight)||50,
      });
      Alert.alert('Created',`Stage "${newStage.name}" created`);
      setShowAddModal(false);setNewStage({name:'',color:'#3B82F6',probability_weight:50,is_active:true});
      await fetchAll(companyId);
    }catch(e){Alert.alert('Error',e.message);}
  };

  const updateStage=async(stage,updates)=>{
    try{
      await api.put(`${EP.stages}/${stage.id}`,updates);
      Alert.alert('Updated');await fetchAll(companyId);
    }catch(e){Alert.alert('Error',e.message);}
  };

  const deleteStage=stage=>{
    const dealsInStage=deals.filter(d=>d.stage===stage.stage_id).length;
    if(dealsInStage>0){Alert.alert('Cannot Delete',`${dealsInStage} deals are in this stage. Move them first.`);return;}
    Alert.alert('Delete Stage',`Delete "${stage.name}"?`,[{text:'Cancel',style:'cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      try{await api.delete(`${EP.stages}/${stage.id}`);Alert.alert('Deleted');await fetchAll(companyId);}catch(e){Alert.alert('Error',e.message);}
    }}]);
  };

  const getDealsInStage=stageId=>deals.filter(d=>d.stage===stageId);
  const getStageValue=stageId=>getDealsInStage(stageId).reduce((s,d)=>s+parseFloat(d.value||0),0);
  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);

  const renderHeader=()=>(
    <LinearGradient colors={['#0F172A','#1E293B']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.hBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={{flex:1,marginLeft:12}}>
          <Text style={st.hTitle}>{companyProfile?.name||'Stage Management'}</Text>
          <Text style={st.hSub}>{stages.length} stages · {deals.length} deals</Text>
        </View>
        <TouchableOpacity onPress={()=>setShowAddModal(true)} style={st.hBtn}><MaterialCommunityIcons name="plus" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderAnalytics=()=>(
    <View style={st.analyticsRow}>
      {[{l:'Total Deals',v:stageAnalytics.totalDeals,c:'#0F172A'},{l:'Win Rate',v:`${stageAnalytics.avgConversion.toFixed(1)}%`,c:'#10B981'},
        {l:'Avg Cycle',v:`${stageAnalytics.avgCycleTime}d`,c:'#F59E0B'},{l:'Bottleneck',v:stageAnalytics.bottleneck?.stage||'N/A',c:'#EF4444'}].map((s2,i)=>(
        <View key={i} style={st.analyticsCard}><Text style={[st.analyticsVal,{color:s2.c}]}>{s2.v}</Text><Text style={st.analyticsLbl}>{s2.l}</Text></View>
      ))}
    </View>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'stages',t:'Stages',i:'format-list-numbered'},{k:'flow',t:'Flow',i:'arrow-decision'},{k:'deals',t:'Deals by Stage',i:'chart-bar'},{k:'settings',t:'Settings',i:'cog'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#0F172A':'#6B7280'}/>
          <Text style={[st.tabTxt,activeTab===tab.k&&{color:'#0F172A',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderStages=()=>(
    <View style={{padding:16}}>
      {stages.map((stg,idx)=>{
        const count=getDealsInStage(stg.stage_id).length;
        const value=getStageValue(stg.stage_id);
        return (
          <TouchableOpacity key={idx} style={[st.stageCard,{borderLeftColor:stg.color||'#6B7280',borderLeftWidth:4}]}
            onPress={()=>{setSelectedStage(stg);setShowStageModal(true);}}>
            <View style={st.stageHeader}>
              <View style={{flexDirection:'row',alignItems:'center',flex:1}}>
                <View style={[st.orderBadge,{backgroundColor:stg.color||'#6B7280'}]}><Text style={st.orderNum}>{stg.display_order||idx+1}</Text></View>
                <View style={{marginLeft:10}}>
                  <Text style={st.stageName}>{stg.name}</Text>
                  <Text style={st.stageMeta}>{stg.stage_id} · {stg.probability_weight||0}% probability</Text>
                </View>
              </View>
              <View style={{alignItems:'flex-end'}}>
                <Text style={st.stageCount}>{count} deals</Text>
                <Text style={st.stageVal}>{formatCurrency(value)}</Text>
              </View>
            </View>
            {!stg.is_active&&<View style={st.inactiveBadge}><Text style={st.inactiveText}>Inactive</Text></View>}
            <View style={st.stageBar}><View style={[st.stageFill,{width:`${Math.min((count/Math.max(deals.length,1))*100,100)}%`,backgroundColor:stg.color||'#6B7280'}]}/></View>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderFlow=()=>(
    <View style={st.section}>
      <Text style={st.secTitle}>Pipeline Flow</Text>
      {stages.filter(s=>s.is_active!==false).map((stg,idx)=>{
        const count=getDealsInStage(stg.stage_id).length;
        const nextStage=stages[idx+1];
        const nextCount=nextStage?getDealsInStage(nextStage.stage_id).length:0;
        const convRate=count>0&&nextCount>0?((nextCount/count)*100).toFixed(0):'-';
        return (
          <View key={idx}>
            <View style={st.flowStage}>
              <View style={[st.flowDot,{backgroundColor:stg.color}]}/>
              <View style={{flex:1,marginLeft:12}}>
                <View style={{flexDirection:'row',justifyContent:'space-between'}}>
                  <Text style={st.flowName}>{stg.name}</Text>
                  <Text style={st.flowCount}>{count} deals · {formatCurrency(getStageValue(stg.stage_id))}</Text>
                </View>
                <View style={st.flowBar}><View style={[st.flowFill,{width:`${deals.length>0?(count/deals.length*100):0}%`,backgroundColor:stg.color}]}/></View>
              </View>
            </View>
            {nextStage&&stg.stage_id!=='closed_won'&&stg.stage_id!=='closed_lost'&&(
              <View style={st.flowArrow}><MaterialCommunityIcons name="arrow-down" size={16} color="#9CA3AF"/>
                <Text style={st.convRate}>{convRate}% conversion</Text></View>
            )}
          </View>
        );
      })}
    </View>
  );

  const renderDealsByStage=()=>(
    <View style={{padding:16}}>
      {stages.map((stg,idx)=>{
        const stgDeals=getDealsInStage(stg.stage_id);
        if(stgDeals.length===0)return null;
        return (
          <View key={idx} style={st.section}>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <View style={{flexDirection:'row',alignItems:'center'}}>
                <View style={[st.dot,{backgroundColor:stg.color}]}/>
                <Text style={st.secTitle}>{stg.name}</Text>
              </View>
              <Text style={st.stageVal}>{formatCurrency(getStageValue(stg.stage_id))}</Text>
            </View>
            {stgDeals.map((d,i)=>(
              <View key={i} style={st.dealCard}>
                <View style={{flexDirection:'row',justifyContent:'space-between'}}>
                  <View style={{flex:1}}><Text style={st.dealName}>{d.contact_name||'Unknown'}</Text><Text style={st.dealMeta}>{d.company||''} · {d.source||'direct'}</Text></View>
                  <Text style={st.dealVal}>{formatCurrency(d.value)}</Text>
                </View>
              </View>
            ))}
          </View>
        );
      })}
    </View>
  );

  const renderSettings=()=>(
    <View style={st.section}>
      <Text style={st.secTitle}>Pipeline Settings</Text>
      <View style={st.settingCard}>
        <MaterialCommunityIcons name="information" size={20} color="#3B82F6"/>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={st.settingTitle}>Total Stages: {stages.length}</Text>
          <Text style={st.settingDesc}>Active: {stages.filter(s=>s.is_active!==false).length} · Inactive: {stages.filter(s=>s.is_active===false).length}</Text>
        </View>
      </View>
      {companyProfile&&(
        <View style={st.settingCard}>
          <MaterialCommunityIcons name="office-building" size={20} color="#10B981"/>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={st.settingTitle}>{companyProfile.name}</Text>
            <Text style={st.settingDesc}>{companyProfile.industry||''}{companyProfile.headquarters?` · ${companyProfile.headquarters}`:''}</Text>
          </View>
        </View>
      )}
      <TouchableOpacity style={st.addStageBtn} onPress={()=>setShowAddModal(true)}>
        <MaterialCommunityIcons name="plus" size={20} color="#FFF"/><Text style={st.addStageBtnTxt}>Add New Stage</Text>
      </TouchableOpacity>
    </View>
  );

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0F172A']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){
      case 'stages':return wrap(renderStages());
      case 'flow':return wrap(renderFlow());
      case 'deals':return wrap(renderDealsByStage());
      case 'settings':return wrap(renderSettings());
      default:return null;
    }
  };

  const renderStageDetailModal=()=>(
    <Modal visible={showStageModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowStageModal(false)}>
      <View style={st.modal}><View style={st.modalH}>
        <Text style={st.modalTitle}>{selectedStage?.name||'Stage'}</Text>
        <TouchableOpacity style={st.modalClose} onPress={()=>setShowStageModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
      </View>
      {selectedStage&&<ScrollView style={{flex:1,padding:16}}>
        <View style={st.mSec}><Text style={st.mSecTitle}>Stage Details</Text>
          <Text style={st.mTxt}>ID: {selectedStage.stage_id}</Text><Text style={st.mTxt}>Name: {selectedStage.name}</Text>
          <Text style={st.mTxt}>Probability: {selectedStage.probability_weight}%</Text>
          <Text style={st.mTxt}>Order: {selectedStage.display_order}</Text>
          <Text style={st.mTxt}>Active: {selectedStage.is_active!==false?'Yes':'No'}</Text>
          <View style={{flexDirection:'row',alignItems:'center',marginTop:4}}><Text style={st.mTxt}>Color: </Text><View style={{width:20,height:20,borderRadius:4,backgroundColor:selectedStage.color||'#6B7280'}}/></View>
        </View>
        <View style={st.mSec}><Text style={st.mSecTitle}>Deals in Stage</Text>
          <Text style={st.mTxt}>{getDealsInStage(selectedStage.stage_id).length} deals · {formatCurrency(getStageValue(selectedStage.stage_id))}</Text>
        </View>
        <View style={{flexDirection:'row',gap:8,marginTop:16}}>
          <TouchableOpacity style={[st.actionBtn,{backgroundColor:'#FEF2F2',flex:1}]} onPress={()=>{setShowStageModal(false);deleteStage(selectedStage);}}>
            <MaterialCommunityIcons name="delete" size={18} color="#EF4444"/><Text style={{color:'#EF4444',fontWeight:'600',marginLeft:6}}>Delete</Text></TouchableOpacity>
          <TouchableOpacity style={[st.actionBtn,{backgroundColor:selectedStage.is_active!==false?'#FEF2F2':'#ECFDF5',flex:1}]}
            onPress={()=>{updateStage(selectedStage,{is_active:selectedStage.is_active===false});setShowStageModal(false);}}>
            <MaterialCommunityIcons name={selectedStage.is_active!==false?'close':'check'} size={18} color={selectedStage.is_active!==false?'#EF4444':'#10B981'}/>
            <Text style={{color:selectedStage.is_active!==false?'#EF4444':'#10B981',fontWeight:'600',marginLeft:6}}>{selectedStage.is_active!==false?'Deactivate':'Activate'}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>}</View>
    </Modal>
  );

  const renderAddModal=()=>(
    <Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAddModal(false)}>
      <View style={st.modal}><View style={st.modalH}>
        <Text style={st.modalTitle}>Add Stage</Text>
        <TouchableOpacity style={st.modalClose} onPress={()=>setShowAddModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
      </View>
      <ScrollView style={{flex:1,padding:16}}>
        <View style={st.formField}><Text style={st.formLbl}>Stage Name *</Text>
          <TextInput style={st.formInput} placeholder="e.g. Discovery" placeholderTextColor="#9CA3AF" value={newStage.name} onChangeText={v=>setNewStage({...newStage,name:v})}/></View>
        <View style={st.formField}><Text style={st.formLbl}>Probability Weight (%)</Text>
          <TextInput style={st.formInput} placeholder="50" placeholderTextColor="#9CA3AF" value={String(newStage.probability_weight)} onChangeText={v=>setNewStage({...newStage,probability_weight:v})} keyboardType="numeric"/></View>
        <View style={st.formField}><Text style={st.formLbl}>Color</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:8}}>
            {colorOptions.map((c,i)=>(
              <TouchableOpacity key={i} style={[st.colorDot,{backgroundColor:c},newStage.color===c&&{borderWidth:3,borderColor:'#1F2937'}]} onPress={()=>setNewStage({...newStage,color:c})}/>
            ))}
          </View></View>
        <View style={st.formField}>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
            <Text style={st.formLbl}>Active</Text>
            <Switch value={newStage.is_active} onValueChange={v=>setNewStage({...newStage,is_active:v})} trackColor={{true:'#10B981'}}/>
          </View></View>
        <TouchableOpacity style={st.submitBtn} onPress={createStage}>
          <MaterialCommunityIcons name="check" size={20} color="#FFF"/><Text style={st.submitBtnTxt}>Create Stage</Text></TouchableOpacity>
      </ScrollView></View>
    </Modal>
  );

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#0F172A"/><Text style={st.loadingTxt}>Loading stages...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderAnalytics()}{renderTabs()}{renderContent()}{renderStageDetailModal()}{renderAddModal()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingTxt:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},hBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},hTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},hSub:{fontSize:13,color:'#94A3B8',marginTop:2},
  analyticsRow:{flexDirection:'row',backgroundColor:'#FFF',padding:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB',justifyContent:'space-around'},analyticsCard:{alignItems:'center'},analyticsVal:{fontSize:16,fontWeight:'bold'},analyticsLbl:{fontSize:10,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#F1F5F9'},tabTxt:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},secTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12,marginLeft:8},
  stageCard:{backgroundColor:'#FFF',marginBottom:10,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},
  stageHeader:{flexDirection:'row',justifyContent:'space-between',alignItems:'center'},
  orderBadge:{width:28,height:28,borderRadius:14,justifyContent:'center',alignItems:'center'},orderNum:{color:'#FFF',fontWeight:'bold',fontSize:12},
  stageName:{fontSize:16,fontWeight:'600',color:'#1F2937'},stageMeta:{fontSize:12,color:'#6B7280',marginTop:1},
  stageCount:{fontSize:14,fontWeight:'600',color:'#1F2937'},stageVal:{fontSize:13,fontWeight:'600',color:'#10B981'},
  inactiveBadge:{backgroundColor:'#FEF2F2',borderRadius:6,paddingHorizontal:8,paddingVertical:2,alignSelf:'flex-start',marginTop:6},inactiveText:{color:'#EF4444',fontSize:11,fontWeight:'600'},
  stageBar:{height:4,backgroundColor:'#E5E7EB',borderRadius:2,marginTop:10,overflow:'hidden'},stageFill:{height:'100%',borderRadius:2},
  dot:{width:10,height:10,borderRadius:5,marginRight:6},
  flowStage:{flexDirection:'row',alignItems:'center',marginBottom:4},flowDot:{width:16,height:16,borderRadius:8},flowName:{fontSize:14,fontWeight:'600',color:'#1F2937'},flowCount:{fontSize:12,color:'#6B7280'},
  flowBar:{height:6,backgroundColor:'#E5E7EB',borderRadius:3,marginTop:4,overflow:'hidden'},flowFill:{height:'100%',borderRadius:3},
  flowArrow:{flexDirection:'row',alignItems:'center',paddingLeft:4,paddingVertical:4},convRate:{fontSize:11,color:'#9CA3AF',marginLeft:4},
  dealCard:{backgroundColor:'#F9FAFB',borderRadius:8,padding:10,marginBottom:4},dealName:{fontSize:14,fontWeight:'600',color:'#1F2937'},dealMeta:{fontSize:12,color:'#6B7280',marginTop:1},dealVal:{fontSize:13,fontWeight:'700',color:'#10B981'},
  settingCard:{flexDirection:'row',alignItems:'center',backgroundColor:'#F9FAFB',borderRadius:10,padding:14,marginBottom:8},settingTitle:{fontSize:15,fontWeight:'600',color:'#1F2937'},settingDesc:{fontSize:12,color:'#6B7280',marginTop:1},
  addStageBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#0F172A',borderRadius:10,padding:14,marginTop:12},addStageBtnTxt:{color:'#FFF',fontWeight:'700',fontSize:15,marginLeft:8},
  modal:{flex:1,backgroundColor:'#FFF'},modalH:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  mSec:{marginBottom:20},mSecTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},mTxt:{fontSize:14,color:'#6B7280',lineHeight:22},
  actionBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',padding:12,borderRadius:8},
  formField:{marginBottom:16},formLbl:{fontSize:14,fontWeight:'600',color:'#1F2937',marginBottom:6},formInput:{backgroundColor:'#F3F4F6',borderRadius:8,padding:12,fontSize:14,color:'#1F2937',borderWidth:1,borderColor:'#E5E7EB'},
  colorDot:{width:32,height:32,borderRadius:16},
  submitBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#0F172A',borderRadius:10,padding:14,marginTop:8},submitBtnTxt:{color:'#FFF',fontWeight:'700',fontSize:16,marginLeft:8},
});